import { Button, Empty, message, Modal, Popconfirm, Spin, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { TbChartLine, TbEdit, TbPlus, TbSend, TbTrash, TbX } from "react-icons/tb";
import { ApiService } from "api/api.service";
import { ApplicationDto, ManagerApprovalStatus } from "dtos/application.dto";
import { StrategyCardFooterTags } from "pages/Organisation/StrategyModal";
import { parseNumericToInternationalFormat } from "utils/functions";
import { queryClient } from "utils/queryClient";
import { isSelectedStrategy, toStrategyConfig, toWizardValues } from "./functions";
import StrategyPreviewSections from "./StrategyPreviewSections";
import StrategySummary from "./StrategySummary";

const SavedStrategiesModal = ({
  open,
  appkey,
  selected,
  focusIndex,
  onClose,
  onAdd,
  onEdit,
}: {
  open: boolean;
  appkey: string | undefined;
  /** Slug of the strategy the customer selected. */
  selected?: string;
  /** Strategy to show when the modal opens or after a save. */
  focusIndex?: number;
  onClose: () => void;
  onAdd: () => void;
  onEdit: (index: number) => void;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const applicationResponse = ApiService.application.useGetByAppKey(appkey);
  const getCustomStrategy = ApiService.application.useGetCustomStrategies(appkey);
  const saveCustomStrategy = ApiService.application.useSaveCustomStrategiesV2();
  const sendStrategy = ApiService.application.useSendCustomStrategies();
  const rejectApproval = ApiService.application.useRejectManagerApproval();

  const application = applicationResponse.data?.application as ApplicationDto | undefined;
  const organisation = ApiService.organisation.useGetBySlug(application?.organisation?.code);
  const strategies = getCustomStrategy.data?.result.v2 ?? [];
  const index = Math.min(activeIndex, Math.max(strategies.length - 1, 0));
  const active = strategies[index];
  const activeSelected = isSelectedStrategy(active, index, selected);
  const activeConfig = useMemo(() => (active ? toStrategyConfig(toWizardValues(active)) : undefined), [active]);
  const templateName = organisation.data?.pandadoc_mappings?.find((m) => m._id === active?.template_id)?.title;

  useEffect(() => {
    if (open && focusIndex != null) setActiveIndex(focusIndex);
  }, [open, focusIndex]);

  const removeActive = () => {
    const body = strategies.filter((_, i) => i !== index);
    saveCustomStrategy.mutate(
      { appkey: appkey!, body },
      {
        onSuccess: async () => {
          message.success("Strategy deleted.");
          queryClient.invalidateQueries({ queryKey: ["application"] });
          await getCustomStrategy.refetch();
          setActiveIndex((prev) => Math.max(0, Math.min(prev, body.length - 1)));
        },
      },
    );
  };

  const reject = (cancel: boolean) =>
    rejectApproval.mutate(
      { appkey: appkey as string, cancel },
      {
        onSuccess: () => {
          message.success(cancel ? "Manager's approval has been cancelled." : "Manager's approval has been rejected.");
          applicationResponse.refetch();
        },
      },
    );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={1200}
      destroyOnHidden
      styles={{ content: { padding: 0, overflow: "hidden" } }}
    >
      <div className="flex flex-col h-[min(800px,86vh)]">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-7 pt-5 pb-4 border-0 border-b border-solid border-gray-200">
          <div>
            <Typography.Title level={4} className="!m-0">
              Custom strategies
            </Typography.Title>
            <Typography.Text className="!text-[13px] !text-gray-600">
              Plans prepared for this application. Send them to the debtor portal when they’re ready.
            </Typography.Text>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex flex-col items-end mr-2">
              <span className="text-xs text-gray-600">Customer budget</span>
              {application?.approval_requested ? (
                <span className="text-base font-semibold text-emerald-700">
                  ${parseNumericToInternationalFormat(application.requested_budget)}
                </span>
              ) : (
                <span className="text-base font-semibold text-red-600">N/A</span>
              )}
            </div>
            {application?.approval_requested &&
              application.manager_approval_status &&
              application.manager_approval_status !== ManagerApprovalStatus.REJECTED && (
                <Popconfirm
                  title="Are you sure to reject the request?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => reject(false)}
                >
                  <Button
                    danger
                    size="small"
                    loading={rejectApproval.isPending && rejectApproval.variables?.cancel === false}
                  >
                    Reject approval
                  </Button>
                </Popconfirm>
              )}
            {application?.approval_requested && (
              <Popconfirm
                title="Are you sure to cancel the request?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => reject(true)}
              >
                <Button
                  danger
                  size="small"
                  loading={rejectApproval.isPending && rejectApproval.variables?.cancel === true}
                >
                  Cancel approval
                </Button>
              </Popconfirm>
            )}
            <Button type="text" aria-label="Close" icon={<TbX size={18} />} onClick={onClose} />
          </div>
        </div>

        {/* Body */}
        {getCustomStrategy.isPending || applicationResponse.isPending ? (
          <div className="grow flex items-center justify-center">
            <Spin />
          </div>
        ) : strategies.length === 0 ? (
          <div className="grow flex items-center justify-center">
            <Empty description="No custom strategies created yet">
              <Button type="primary" icon={<TbPlus />} onClick={onAdd}>
                Create your first strategy
              </Button>
            </Empty>
          </div>
        ) : (
          <div className="grow flex min-h-0">
            {/* List */}
            <div className="w-80 shrink-0 flex flex-col min-h-0 border-0 border-r border-solid border-gray-200">
              <div className="px-4 pt-3.5 pb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-600">
                Saved · {strategies.length}
              </div>
              <div className="grow overflow-y-auto px-2.5 py-1 flex flex-col gap-1">
                {strategies.map((strategy, i) => {
                  const isActive = i === index;
                  return (
                    <button
                      key={strategy.slug ?? i}
                      type="button"
                      onClick={() => setActiveIndex(i)}
                      className={`flex gap-3 items-start text-left font-[inherit] w-full border-0 cursor-pointer p-3.5 rounded-xl ${
                        isActive ? "bg-violet-50" : "bg-transparent hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`w-10 h-10 rounded-lg shrink-0 flex items-center justify-center text-lg ${
                          isActive ? "bg-white text-violet-700 shadow-sm" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <TbChartLine />
                      </span>
                      <span className="flex flex-col gap-1 min-w-0 grow">
                        <span className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-gray-900 truncate">
                            {strategy.key || `Custom Strategy ${i + 1}`}
                          </span>
                          {isSelectedStrategy(strategy, i, selected) && (
                            <Tag color="green" className="!m-0 uppercase !text-[10px] !leading-4">
                              Selected
                            </Tag>
                          )}
                        </span>
                        <span className="text-xs text-gray-600 truncate">
                          {strategy.description || "No description provided"}
                        </span>
                        {/* <StrategyCardFooterTags
                          values={{
                            resolution_type: strategy.resolution_type,
                            format: strategy.format,
                            payment_type: strategy.payment_type,
                          }}
                          className="mt-1 flex-wrap gap-y-1"
                        /> */}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="px-3.5 py-3 border-0 border-t border-solid border-gray-200">
                <Button block type="dashed" icon={<TbPlus />} onClick={onAdd}>
                  Add new strategy
                </Button>
              </div>
            </div>

            {/* Detail */}
            {active && application && (
              <div className="grow flex flex-col min-w-0">
                <div className="flex justify-between items-start gap-4 px-6 pt-4 pb-3.5 border-0 border-b border-solid border-gray-100">
                  <div>
                    <div className="flex items-center gap-2">
                      <Typography.Title level={5} className="!m-0">
                        {active.key || `Custom Strategy ${index + 1}`}
                      </Typography.Title>
                      {activeSelected && (
                        <Tag color="green" className="!m-0 uppercase">
                          Selected by customer
                        </Tag>
                      )}
                    </div>
                    {active?.description && (
                      <Typography.Text className="!text-[13px] !text-gray-600">{active.description}</Typography.Text>
                    )}
                    <StrategyCardFooterTags
                      values={{
                        resolution_type: active?.resolution_type,
                        format: active?.format,
                        payment_type: active?.payment_type,
                      }}
                      className="mt-1 flex-wrap gap-y-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Popconfirm
                      title="Delete this strategy?"
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      onConfirm={removeActive}
                      disabled={activeSelected}
                    >
                      <Button
                        danger
                        icon={<TbTrash />}
                        disabled={activeSelected}
                        loading={saveCustomStrategy.isPending}
                        title={activeSelected ? "The customer selected this strategy" : undefined}
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                    <Button type="primary" icon={<TbEdit />} onClick={() => onEdit(index)}>
                      Edit
                    </Button>
                  </div>
                </div>
                <div className="grow grid grid-cols-2 min-h-0">
                  <div className="overflow-y-auto pl-6 pr-5 py-4 border-0 border-r border-solid border-gray-100">
                    <StrategySummary strategy={active} templateName={templateName} />
                  </div>
                  <div className="overflow-y-auto pl-5 pr-6 py-4 bg-[#f7f7fa]">
                    <StrategyPreviewSections
                      key={active.slug ?? index}
                      application={application}
                      config={activeConfig!}
                      sections={["overview", "breakdown", "timeline", "ledger"]}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center px-7 py-3.5 border-0 border-t border-solid border-gray-200">
          <Typography.Text className="!text-[13px] !text-gray-600">
            {strategies.length
              ? `${strategies.length} plan${strategies.length > 1 ? "s" : ""} will be sent to the debtor portal.`
              : "Create a plan to send it to the debtor portal."}
          </Typography.Text>
          <Button
            type="primary"
            icon={<TbSend />}
            disabled={strategies.length < 1}
            loading={sendStrategy.isPending}
            onClick={() =>
              sendStrategy.mutate(
                { appkey: appkey! },
                {
                  onSuccess: async () => {
                    message.success("Plans have been sent to debtor portal.");
                    await queryClient.refetchQueries({ queryKey: ["get-strategy"] });
                    onClose();
                  },
                },
              )
            }
          >
            Send plans
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SavedStrategiesModal;
